/**
 * THE VOID ALPHA — primeiro Boss. Quadrúpede procedural com máquina de estados clara e
 * 4 ataques TELEGRAFADOS (Claw Combo, Void Charge, Void Howl + summon, Void Leap), 2 fases
 * (50% = Void Rage, 20% = pressão final) e anti-chain (respiro entre ataques). NÃO é um
 * inimigo comum com HP alto: exige leitura de telegraph, dodge, posicionamento e timing.
 * O Hook só o INTERROMPE durante a janela do Howl; fora disso ele não é puxável.
 */
import * as THREE from "three";
import { applySkin } from "../enemies/CreatureSkins";
import { applyRim } from "../fx/RimLight";
import { GameConfig } from "../config/GameConfig";
import type { Damageable } from "../combat/Damageable";
import type { HookReaction, HookTarget } from "../player/HookSystem";
import type { TelegraphSystem } from "../fx/Telegraph";
import type { ParticleSystem } from "../fx/Particles";
import { HealthComponent } from "../combat/HealthComponent";
import { dealDamage } from "../combat/DamageSystem";
import { events } from "../core/Events";
import { dampAngle } from "../utils/math";
import type { AlphaForm } from "./BossForms";
import { furyMult } from "./Fury"; // 10.9BF

export type BossState = "intro" | "chase" | "attack" | "stagger" | "phaseTransition" | "dead";
export type BossAttack = "clawCombo" | "charge" | "howl" | "leap" | "spikes";

export interface VoidAlphaDeps {
  player: Damageable;
  telegraph: TelegraphSystem;
  particles: ParticleSystem;
  /** Invoca `count` adds perto de `center` (o Game respeita o teto de maxAdds). */
  summon: (count: number, center: THREE.Vector3) => void;
  /** Impulso sutil de câmera em impactos grandes. */
  impulse: (strength: number) => void;
}

/** 10.4R — variante de EVENTO: mesma IA/arena, buffado, loot exclusivo, evento próprio. */
export interface VoidAlphaOptions {
  event?: boolean; // true = boss de evento (emite "boss:defeatedEvent")
  hpMult?: number; // multiplicador de vida (fica mais tanky)
  dmgMult?: number; // multiplicador de dano
  spawnAt?: { x: number; z: number }; // centro da arena desta instância (default: arena do Alpha)
  tint?: number; // 10.9AC — cor da variante (aura/emissivo) — "boss diferente toda vez"
  titleKey?: string; // 10.9AC — chave i18n do nome exibido no HUD (default: boss.voidAlpha)
  arenaRadius?: number; // 10.9AJ — raio da arena DESTA instância (salas da Zona dos Bosses são menores)
  form?: AlphaForm; // 10.9AT — morfologia da variante (asas, coroa, lâminas, placas, chifres, escala)
}

const cfg = GameConfig.boss.voidAlpha;
const DANGER = 0xff3344;
const AURA = 0x9d3bff;

const _v = new THREE.Vector3();

export class VoidAlpha implements Damageable, HookTarget {
  readonly group = new THREE.Group();
  readonly health: HealthComponent;
  readonly radius = cfg.radius;
  readonly pulled = false; // o Boss nunca é puxado

  state: BossState = "intro";
  removeRequested = false;

  private stateTimer: number = cfg.introDuration;
  private attackName: BossAttack | null = null;
  private atkPhase = 0;
  private atkTimer = 0;
  private phase = 1 as number;
  private restTimer = 0;
  private reactStagger = 0;
  private facing = Math.PI; // encara o sul (o player chega pelo sul)
  private flash = 0;
  private deathTimer = 0;

  // 10.9AB — ANTECIPAÇÃO: rastreia a velocidade do player p/ leap/charge acertarem quem foge
  // (classes à distância faziam kite infinito e os ataques caíam onde o player JÁ NÃO estava).
  private readonly lastPlayerPos = new THREE.Vector3();
  private readonly playerVel = new THREE.Vector3();
  private velInit = false;

  private readonly cd = { clawCombo: 0, charge: 0, howl: 0, leap: 0, spikes: 0 };
  // 10.9AV — animação procedural do boss (peso/antecipação)
  private readonly legMeshes: THREE.Mesh[] = [];
  private headMesh: THREE.Mesh | null = null;
  private bodyMesh: THREE.Mesh | null = null;
  private tailMesh: THREE.Mesh | null = null;
  private gait = 0;
  private readonly lastPos = new THREE.Vector3();
  private moveSpeedEst = 0;
  // 10.9AF — erupções à distância pendentes (resolvem o dano após o telegraph)
  private readonly pendingSpikes: { x: number; z: number; r: number; t: number; dmg: number }[] = [];

  // charge
  private readonly chargeDir = new THREE.Vector3();
  private chargeTravelled = 0;
  private chargeHitDone = false;
  // leap
  private readonly target = new THREE.Vector3();
  private readonly leapStart = new THREE.Vector3();
  private leapT = 0;
  private leapRadius: number = cfg.leap.radius;
  // howl
  private howlWindup = 0;
  // handle do telegraph do ataque atual (p/ cancelar em abort/interrupt)
  private atkTeleId = 0;

  private readonly bodyMat: THREE.MeshStandardMaterial;
  /** 10.5X — veias de energia (pulsam com a fase de fúria). */
  private readonly veinMats: THREE.MeshStandardMaterial[] = [];
  /** 10.9BT — materiais secundários (placas/chifres) que recebem a luz de borda da variante. */
  private readonly rimMats: THREE.MeshStandardMaterial[] = [];
  private readonly eyeMat: THREE.MeshBasicMaterial;
  private readonly auraMat: THREE.MeshBasicMaterial;
  private auraPulse = 0;

  // 10.4R — variante de evento (arena por instância + buffs + evento próprio)
  private readonly isEvent: boolean;
  private readonly baseDmgMult: number;
  /** 10.9BF — dano × FÚRIA (vida baixa bate mais) */
  private get dmgMult(): number { return this.baseDmgMult * furyMult(this.health.fraction); }
  private readonly arenaCx: number;
  private readonly arenaCz: number;
  private readonly titleKey?: string; // 10.9AC — nome da variante no HUD
  private readonly arenaR: number; // 10.9AJ — raio da arena desta instância

  constructor(
    scene: THREE.Scene,
    private readonly deps: VoidAlphaDeps,
    opts?: VoidAlphaOptions,
  ) {
    this.isEvent = opts?.event === true;
    this.baseDmgMult = opts?.dmgMult ?? 1;
    this.arenaCx = opts?.spawnAt?.x ?? cfg.arena.center.x;
    this.arenaCz = opts?.spawnAt?.z ?? cfg.arena.center.z;
    this.titleKey = opts?.titleKey;
    this.arenaR = opts?.arenaRadius ?? cfg.arena.radius;
    this.health = new HealthComponent(Math.round(cfg.maxHp * (opts?.hpMult ?? 1)), undefined, () => this.die());

    this.bodyMat = new THREE.MeshStandardMaterial({
      color: 0x2a0f45,
      emissive: 0x6a1fb0,
      emissiveIntensity: 0.5,
      roughness: 0.55,
      flatShading: true,
    });
    this.eyeMat = new THREE.MeshBasicMaterial({ color: 0xff3b6b });
    this.buildMesh();

    this.auraMat = new THREE.MeshBasicMaterial({
      color: AURA,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const aura = new THREE.Mesh(new THREE.RingGeometry(this.radius * 1.4, this.radius * 1.9, 40), this.auraMat);
    aura.rotation.x = -Math.PI / 2;
    aura.position.y = 0.04;
    this.group.add(aura);

    this.group.position.set(this.arenaCx, 0, this.arenaCz);
    if (this.isEvent) {
      // sinal visual do boss de evento: maior e com aura mais intensa
      this.group.scale.setScalar(1.18);
      this.auraMat.color.setHex(0x2be0ff);
      this.auraMat.opacity = 0.55;
    }
    // 10.9AC — variante do evento: tinge aura + emissivo do corpo (cada aparição é "outro boss")
    if (opts?.tint !== undefined) {
      this.auraMat.color.setHex(opts.tint);
      this.bodyMat.emissive.setHex(opts.tint);
      this.eyeMat.color.setHex(opts.tint);
    }
    // 10.9BT — luz de borda na cor da variante (o boss se descola da arena vista de cima)
    const rimTint = opts?.tint ?? AURA;
    applyRim(this.bodyMat, { color: rimTint, power: 2.4, strength: 1.1 });
    for (const m of this.rimMats) applyRim(m, { color: rimTint, power: 2.4, strength: 1.0 });
    if (opts?.form) this.applyForm(opts.form, opts.tint ?? AURA);
    scene.add(this.group);
    events.emit("boss:spawned", undefined);
  }

  private buildMesh(): void {
    const armorMat = new THREE.MeshStandardMaterial({ color: 0x49306b, emissive: 0x21083d, emissiveIntensity: 0.5, roughness: 0.32, metalness: 0.65, flatShading: true });
    const hornMat = new THREE.MeshStandardMaterial({ color: 0xc7b6de, roughness: 0.46, metalness: 0.38, flatShading: true });
    // 10.5X — pele/placas com RELEVO (o boss era liso demais perto das criaturas novas)
    applySkin(this.bodyMat, "hide", 0.05, 4);
    applySkin(armorMat, "chitin", 0.04, 3);
    this.rimMats.push(armorMat, hornMat);
    // 10.9BT: veias 2.2 → 1.2 em repouso (bloom threshold 0.85); o pulso no update também respeita o teto
    const veinMat = new THREE.MeshStandardMaterial({ color: 0xff5a8a, emissive: 0xff2a5a, emissiveIntensity: 1.2, roughness: 0.25, flatShading: true });
    // Corpo orgânico em vez de bloco: peito pesado, carapaça por placas e ventre escuro.
    const body = new THREE.Mesh(new THREE.SphereGeometry(this.radius, 12, 8), this.bodyMat);
    body.scale.set(1.12, 0.82, 1.7);
    body.position.y = this.radius * 1.5;
    body.castShadow = true;
    this.group.add(body);

    // cabeça (frente = +Z)
    const head = new THREE.Mesh(new THREE.DodecahedronGeometry(this.radius * 0.82, 0), armorMat);
    head.scale.set(1, 0.84, 1.18);
    head.rotation.x = Math.PI / 2;
    head.position.set(0, this.radius * 1.7, this.radius * 1.9);
    head.castShadow = true;
    this.group.add(head);
    this.headMesh = head; // 10.9AV — animação (antecipação/lunge)
    this.bodyMesh = body;

    const muzzle = new THREE.Mesh(new THREE.SphereGeometry(this.radius * 0.46, 8, 6), this.bodyMat);
    muzzle.scale.set(1.05, 0.65, 1.12);
    muzzle.position.set(0, this.radius * 1.47, this.radius * 2.42);
    this.group.add(muzzle);

    // olhos
    const eyeGeo = new THREE.SphereGeometry(this.radius * 0.16, 8, 8);
    for (const sx of [-1, 1]) {
      const eye = new THREE.Mesh(eyeGeo, this.eyeMat);
      eye.position.set(sx * this.radius * 0.32, this.radius * 1.85, this.radius * 2.2);
      this.group.add(eye);
    }

    // crista dorsal (silhueta)
    const spineGeo = new THREE.ConeGeometry(this.radius * 0.25, this.radius * 0.8, 5);
    for (let i = 0; i < 5; i++) {
      const plate = new THREE.Mesh(new THREE.DodecahedronGeometry(this.radius * 0.52, 0), armorMat);
      plate.scale.set(1.05, 0.32, 0.62);
      plate.position.set(0, this.radius * 2.12, this.radius * (1.15 - i * 0.52));
      plate.rotation.z = (i % 2 ? -1 : 1) * 0.08;
      this.group.add(plate);
      const spike = new THREE.Mesh(spineGeo, hornMat);
      spike.position.set(0, this.radius * 2.35, this.radius * (1.1 - i * 0.7));
      this.group.add(spike);
    }

    for (const side of [-1, 1]) {
      const horn = new THREE.Mesh(new THREE.ConeGeometry(this.radius * 0.16, this.radius * 1.05, 5), hornMat);
      horn.position.set(side * this.radius * 0.67, this.radius * 2.18, this.radius * 1.85);
      horn.rotation.z = side * 0.64;
      horn.rotation.x = -0.32;
      this.group.add(horn);
    }

    // 4 patas
    const legGeo = new THREE.CylinderGeometry(this.radius * 0.24, this.radius * 0.34, this.radius * 1.25, 7);
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const leg = new THREE.Mesh(legGeo, this.bodyMat);
        leg.position.set(sx * this.radius * 0.7, this.radius * 0.75, sz * this.radius * 1.1);
        leg.castShadow = true;
        this.group.add(leg);
        this.legMeshes.push(leg); // 10.9AV — passada (gait)
        const paw = new THREE.Mesh(new THREE.DodecahedronGeometry(this.radius * 0.35, 0), armorMat);
        paw.scale.set(1, 0.42, 1.25);
        paw.position.set(sx * this.radius * 0.72, this.radius * 0.15, sz * this.radius * 1.17 + this.radius * 0.16);
        this.group.add(paw);
        // 10.5X — GARRAS na frente de cada pata
        const clawGeo = new THREE.ConeGeometry(this.radius * 0.07, this.radius * 0.3, 4);
        for (const k of [-1, 0, 1]) {
          const claw = new THREE.Mesh(clawGeo, hornMat);
          claw.position.set(sx * this.radius * 0.72 + k * this.radius * 0.16, this.radius * 0.12, sz * this.radius * 1.17 + this.radius * (sz > 0 ? 0.5 : -0.2));
          claw.rotation.x = sz > 0 ? Math.PI / 2 : -Math.PI / 2;
          this.group.add(claw);
        }
      }
    }

    // ---------------- 10.5X: JUBA, MANDÍBULA, VEIAS e CAUDA ----------------
    // juba: coroa de espinhos ao redor do pescoço (silhueta de alfa)
    const maneGeo = new THREE.ConeGeometry(this.radius * 0.17, this.radius * 0.75, 4);
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      const spike = new THREE.Mesh(maneGeo, hornMat);
      spike.position.set(Math.cos(a) * this.radius * 0.85, this.radius * 1.72 + Math.sin(a) * this.radius * 0.5, this.radius * 1.3);
      spike.rotation.z = -a + Math.PI / 2;
      spike.rotation.x = -0.5;
      this.group.add(spike);
    }
    // mandíbula + presas
    const jaw = new THREE.Mesh(new THREE.BoxGeometry(this.radius * 0.7, this.radius * 0.22, this.radius * 0.8), this.bodyMat);
    jaw.position.set(0, this.radius * 1.27, this.radius * 2.35);
    this.group.add(jaw);
    const fangGeo = new THREE.ConeGeometry(this.radius * 0.075, this.radius * 0.34, 4);
    for (const sx of [-1, 1]) {
      for (const [ox, oz] of [[0.2, 2.62], [0.36, 2.36]] as const) {
        const fang = new THREE.Mesh(fangGeo, hornMat);
        fang.position.set(sx * this.radius * ox, this.radius * 1.32, this.radius * oz);
        fang.rotation.x = Math.PI;
        this.group.add(fang);
      }
    }
    // veias de energia no lombo (pulsam junto com a aura no update)
    for (let i = 0; i < 6; i++) {
      const vein = new THREE.Mesh(new THREE.OctahedronGeometry(this.radius * 0.13, 0), veinMat);
      vein.position.set((i % 2 ? 1 : -1) * this.radius * 0.55, this.radius * 2.0, this.radius * (1.2 - i * 0.42));
      vein.scale.set(1, 0.5, 1.5);
      this.group.add(vein);
      this.veinMats.push(veinMat);
    }
    // cauda com ponta espinhada
    const tail = new THREE.Mesh(new THREE.ConeGeometry(this.radius * 0.3, this.radius * 2.1, 6), this.bodyMat);
    tail.rotation.x = Math.PI / 2.2;
    tail.position.set(0, this.radius * 1.55, -this.radius * 2.3);
    this.group.add(tail);
    this.tailMesh = tail; // 10.9AV — balanço
    const tailTip = new THREE.Mesh(new THREE.DodecahedronGeometry(this.radius * 0.3, 0), hornMat);
    tailTip.position.set(0, this.radius * 1.15, -this.radius * 3.2);
    this.group.add(tailTip);
    for (const sx of [-1, 1]) {
      const barb = new THREE.Mesh(new THREE.ConeGeometry(this.radius * 0.1, this.radius * 0.42, 4), hornMat);
      barb.position.set(sx * this.radius * 0.22, this.radius * 1.2, -this.radius * 3.2);
      barb.rotation.z = sx * -1.1;
      this.group.add(barb);
    }
  }

  /** 10.9AT — partes EXTRAS por variante (silhueta própria): asas, coroa, lâminas na cauda, placas,
   * chifres maiores e escala por eixo. Aditivo ao corpo base — o esqueleto/IA não mudam. */
  private applyForm(f: AlphaForm, tint: number): void {
    const r = this.radius;
    const bone = new THREE.MeshStandardMaterial({ color: 0xd9d2e6, roughness: 0.5, metalness: 0.3, flatShading: true });
    const glow = new THREE.MeshStandardMaterial({ color: tint, emissive: tint, emissiveIntensity: 1.2, roughness: 0.3, flatShading: true }); // 10.9BT: ≤1.2 (bloom)
    if (f.wings) {
      for (const s of [-1, 1]) {
        const wing = new THREE.Group();
        for (let i = 0; i < 3; i++) {
          const fin = new THREE.Mesh(new THREE.BoxGeometry(r * (1.4 - i * 0.3), r * 0.08, r * 0.35), bone);
          fin.position.set(s * r * (0.7 + i * 0.55), r * (2.3 + i * 0.35), r * (0.2 - i * 0.5));
          fin.rotation.z = s * (0.5 + i * 0.2);
          wing.add(fin);
          const web = new THREE.Mesh(new THREE.BoxGeometry(r * 0.5, r * 0.02, r * 0.5), new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthWrite: false }));
          web.position.copy(fin.position).add(new THREE.Vector3(0, -r * 0.05, -r * 0.25));
          wing.add(web);
        }
        this.group.add(wing);
      }
    }
    if (f.crown && f.crown > 0) {
      for (let i = 0; i < f.crown; i++) {
        const a = (i / f.crown) * Math.PI * 2;
        const c = new THREE.Mesh(new THREE.OctahedronGeometry(r * 0.18, 0), glow);
        c.position.set(Math.cos(a) * r * 0.45, r * 2.45, r * 1.85 + Math.sin(a) * r * 0.3);
        c.scale.set(0.6, 1.6, 0.6);
        this.group.add(c);
      }
    }
    if (f.tailBlades && f.tailBlades > 0) {
      for (let i = 0; i < f.tailBlades; i++) {
        const b = new THREE.Mesh(new THREE.ConeGeometry(r * 0.09, r * 0.5, 4), bone);
        b.position.set(0, r * (1.75 - i * 0.12), -r * (2.2 + i * 0.35));
        b.rotation.x = -0.6;
        this.group.add(b);
      }
    }
    if (f.plates) {
      for (const s of [-1, 1]) {
        const p = new THREE.Mesh(new THREE.DodecahedronGeometry(r * 0.55, 0), bone);
        p.position.set(s * r * 0.95, r * 2.05, r * 0.9);
        p.scale.set(1, 0.5, 1.2);
        this.group.add(p);
        const spike = new THREE.Mesh(new THREE.ConeGeometry(r * 0.12, r * 0.7, 4), glow);
        spike.position.set(s * r * 1.05, r * 2.5, r * 0.9);
        spike.rotation.z = s * 0.5;
        this.group.add(spike);
      }
    }
    if (f.hornLen && f.hornLen !== 1) {
      for (const s of [-1, 1]) {
        const h = new THREE.Mesh(new THREE.ConeGeometry(r * 0.14, r * 1.05 * f.hornLen, 5), glow);
        h.position.set(s * r * 0.55, r * 2.3 + r * 0.3 * (f.hornLen - 1), r * 1.7);
        h.rotation.z = s * 0.75;
        h.rotation.x = -0.4;
        this.group.add(h);
      }
    }
    if (f.bodyScale) this.group.scale.multiply(new THREE.Vector3(...f.bodyScale));
  }

  // ---- Damageable ----
  get position(): THREE.Vector3 {
    return this.group.position;
  }
  get isDead(): boolean {
    return this.state === "dead";
  }
  get invulnerable(): boolean {
    return this.state === "intro" || this.state === "phaseTransition" || this.state === "dead";
  }
  get armor(): number {
    return cfg.armor;
  }

  onHit(): void {
    if (this.isDead) return;
    this.flash = 0.12;
  }

  /** O Boss é imóvel: ignora knockback (peso). */
  applyKnockback(): void {
    /* no-op */
  }

  // ---- HookTarget ----
  onHooked(): HookReaction {
    // Interrompe SOMENTE durante a janela do Howl
    if (this.attackName === "howl" && this.atkPhase === 0) {
      const elapsed = this.howlWindup - this.atkTimer;
      const s = this.atkScale;
      if (elapsed >= cfg.howl.interruptStart * s && elapsed <= cfg.howl.interruptEnd * s) {
        this.interruptHowl();
        return "grab";
      }
    }
    // fora de ataque: reação leve (feedback), sem cheese
    if (this.state === "chase") {
      this.reactStagger = cfg.hookReactStagger;
      return "grab";
    }
    return "none"; // ricochete (não puxa, não interrompe)
  }

  // ---- getters p/ HUD / debug ----
  get hpFraction(): number {
    return Math.max(0, this.health.fraction);
  }
  get phaseNumber(): number {
    return this.phase;
  }
  get nameKey(): string {
    return this.titleKey ?? "boss.voidAlpha";
  }
  /** 10.9AK — arena desta instância (o Game mantém o PLAYER dentro dela: sem matar de fora da linha). */
  get arenaInfo(): { x: number; z: number; r: number } {
    return { x: this.arenaCx, z: this.arenaCz, r: this.arenaR };
  }
  private get isRage(): boolean {
    return this.phase >= 3;
  }
  private get atkScale(): number {
    return this.phase >= 3 ? cfg.phase2AttackScale * 0.9 : this.phase === 2 ? cfg.phase2AttackScale : 1;
  }
  private get speedMult(): number {
    return this.phase >= 3 ? cfg.phase2SpeedMult * 1.08 : this.phase === 2 ? cfg.phase2SpeedMult : 1;
  }
  debugSnapshot(): { state: string; attack: string; phase: number; hp: number } {
    return {
      state: this.state,
      attack: this.attackName ?? "-",
      phase: this.phase,
      hp: Math.ceil(this.health.current),
    };
  }

  // ---- loop ----
  update(dt: number): void {
    if (this.state === "dead") {
      this.updateDeath(dt);
      return;
    }

    (Object.keys(this.cd) as BossAttack[]).forEach((k) => (this.cd[k] = Math.max(0, this.cd[k] - dt)));
    this.restTimer = Math.max(0, this.restTimer - dt);
    this.reactStagger = Math.max(0, this.reactStagger - dt);
    this.trackPlayerVel(dt);
    this.updateSpikes(dt); // 10.9AF — erupções à distância resolvem o dano no fim do telegraph
    if (this.flash > 0) {
      this.flash -= dt;
      this.bodyMat.emissiveIntensity = this.flash > 0 ? 1.8 : this.isRage ? 0.9 : 0.5;
    }
    this.auraPulse += dt * (this.isRage ? 6 : 3);
    this.auraMat.opacity = 0.3 + Math.sin(this.auraPulse) * 0.15 + (this.isRage ? 0.15 : 0);
    // 10.5X — as veias acendem junto (na fúria queimam mais)
    if (this.veinMats.length > 0) {
      this.veinMats[0].emissiveIntensity = (this.isRage ? 1.0 : 0.8) + Math.sin(this.auraPulse * 1.4) * 0.2; // 10.9BT: pico 1.2 (bloom)
    }

    this.checkPhase();

    switch (this.state) {
      case "intro":
        this.updateIntro(dt);
        break;
      case "phaseTransition":
        this.updateTransition(dt);
        break;
      case "stagger":
        this.updateStagger(dt);
        break;
      case "chase":
        this.updateChase(dt);
        break;
      case "attack":
        this.updateAttack(dt);
        break;
    }

    this.clampToArena(this.group.position);
    this.updateFacing(dt);
    // NÃO zera Y durante o salto (Void Leap controla a altura no ar); senão fica rasteiro
    if (!(this.attackName === "leap" && this.atkPhase === 1)) this.group.position.y = 0;
    this.group.rotation.y = this.facing;
    this.animateBody(dt);
  }

  /** 10.9AV — PESO e ANTECIPAÇÃO: passada das patas pela velocidade real, respiração, agacha e
   * recua a cabeça no windup, lança a cabeça/corpo à frente na execução, cauda balança. */
  private animateBody(dt: number): void {
    const r = this.radius;
    const p = this.group.position;
    const d = Math.hypot(p.x - this.lastPos.x, p.z - this.lastPos.z);
    this.lastPos.copy(p);
    const sp = dt > 0 ? d / dt : 0;
    this.moveSpeedEst += (sp - this.moveSpeedEst) * Math.min(1, dt * 10);
    this.gait += dt * (4 + this.moveSpeedEst * 1.3);
    const stride = Math.min(0.55, this.moveSpeedEst * 0.09);
    for (let i = 0; i < this.legMeshes.length; i++) {
      const ph = this.gait + (i % 2 ? Math.PI : 0) + (i >= 2 ? Math.PI / 2 : 0);
      this.legMeshes[i].rotation.x = Math.sin(ph) * stride;
      this.legMeshes[i].position.y = r * 0.75 + Math.max(0, Math.sin(ph)) * stride * r * 0.35;
    }
    // antecipação/execução por fase do ataque
    const windup = this.state === "attack" && this.atkPhase === 0;
    const executing = this.state === "attack" && this.atkPhase >= 1 && this.attackName !== "spikes";
    const antic = windup ? 1 : 0;
    const lunge = executing ? 1 : 0;
    if (this.bodyMesh) {
      const breathe = 1 + Math.sin(this.gait * 0.6) * 0.025;
      const crouch = 1 - antic * 0.12 + lunge * 0.06;
      this.bodyMesh.scale.set(1.12 * (1 + antic * 0.04), 0.82 * breathe * crouch, 1.7 * (1 - antic * 0.05 + lunge * 0.06));
      this.bodyMesh.position.y = r * 1.5 * crouch + Math.abs(Math.sin(this.gait)) * stride * r * 0.15;
    }
    if (this.headMesh) {
      const back = antic * -0.35 + lunge * 0.4;
      this.headMesh.position.z += (r * (1.9 + back) - this.headMesh.position.z) * Math.min(1, dt * 12);
      this.headMesh.position.y = r * (1.7 - antic * 0.25 + lunge * 0.08);
      this.headMesh.rotation.x = Math.PI / 2 + antic * 0.25 - lunge * 0.2;
    }
    if (this.tailMesh) this.tailMesh.rotation.y = Math.sin(this.gait * 0.9) * (0.25 + stride * 0.5);
  }

  private updateIntro(dt: number): void {
    this.stateTimer -= dt;
    if (Math.random() < dt * 3) {
      this.deps.particles.burst(this.group.position.x, this.radius * 1.6, this.group.position.z, AURA, { count: 8, speed: 3 });
    }
    if (this.stateTimer <= 0) this.state = "chase";
  }

  private checkPhase(): void {
    if (this.state === "intro" || this.state === "phaseTransition" || this.state === "dead") return;
    const f = this.health.fraction;
    if (this.phase < 3 && f <= cfg.rageAt) this.enterPhase(3);
    else if (this.phase < 2 && f <= cfg.phase2At) this.enterPhase(2);
  }

  private enterPhase(p: number): void {
    this.phase = p;
    this.state = "phaseTransition";
    this.stateTimer = 1.0;
    // aborta o ataque atual: cancela o telegraph pra não deixar "fantasma" no chão
    this.deps.telegraph.cancel(this.atkTeleId);
    this.atkTeleId = 0;
    this.attackName = null;
    this.atkPhase = 0;
    (Object.keys(this.cd) as BossAttack[]).forEach((k) => (this.cd[k] = 0));
    this.deps.impulse(0.14);
    this.deps.particles.burst(this.group.position.x, this.radius * 1.5, this.group.position.z, DANGER, {
      count: 24,
      speed: 8,
      dur: 0.8,
    });
    events.emit("boss:phase", { phase: p });
  }

  private updateTransition(dt: number): void {
    this.stateTimer -= dt;
    this.bodyMat.emissiveIntensity = 1.2 + Math.sin(this.stateTimer * 20) * 0.6;
    if (this.stateTimer <= 0) {
      this.bodyMat.emissiveIntensity = this.isRage ? 0.9 : 0.5;
      this.state = "chase";
    }
  }

  private updateStagger(dt: number): void {
    this.stateTimer -= dt;
    if (this.stateTimer <= 0) this.state = "chase";
  }

  private updateChase(dt: number): void {
    const dist = this.distToPlayer();
    if (this.reactStagger > 0) return; // flinch curto

    if (this.restTimer <= 0) {
      const name = this.selectAttack(dist);
      if (name) {
        this.beginAttack(name);
        return;
      }
    }
    // aproxima do player
    if (dist > cfg.clawCombo.range * 0.8) {
      _v.subVectors(this.deps.player.position, this.group.position);
      _v.y = 0;
      const d = _v.length();
      // 10.9AB — CATCH-UP: o boss anda a 4 e o player a 5, então de longe o player fugia pra
      // sempre (boss "parado" atrás). Quando a distância é grande ele acelera pra alcançar; de
      // perto volta à velocidade normal (mantém a dança de melee e o valor das esquivas).
      const closing = dist > 8 ? cfg.moveSpeed * 0.75 : 0;
      if (d > 0.001) this.group.position.addScaledVector(_v, ((cfg.moveSpeed * this.speedMult + closing) * dt) / d);
    }
  }

  private selectAttack(dist: number): BossAttack | null {
    const opts: BossAttack[] = [];
    if (this.cd.clawCombo <= 0 && dist <= cfg.clawCombo.approachRange) opts.push("clawCombo");
    if (this.cd.charge <= 0 && dist >= 6) opts.push("charge");
    // 10.9AB — Howl é AoE EM VOLTA do boss: só faz sentido de perto. De longe ficava uivando no
    // vazio (parecia "parado sem atacar"). Agora só entra no pool quando o player está no raio.
    if (this.cd.howl <= 0 && dist <= cfg.howl.radius + 1.5) opts.push("howl");
    if (this.cd.leap <= 0 && dist >= 5) opts.push("leap");
    // 10.9AF — ataque À DISTÂNCIA: pune quem fica longe (o boss só machucava no contato). Peso
    // DOBRADO quando o player está fora do alcance de melee, pra ele realmente pressionar de longe.
    if (this.cd.spikes <= 0 && dist > cfg.clawCombo.range + 1) {
      opts.push("spikes");
      if (dist > 8) opts.push("spikes");
    }
    if (opts.length === 0) return null;
    return opts[Math.floor(Math.random() * opts.length)];
  }

  private beginAttack(name: BossAttack): void {
    this.attackName = name;
    this.state = "attack";
    this.atkPhase = 0;
    const s = this.atkScale;
    const pos = this.group.position;
    switch (name) {
      case "clawCombo":
        this.atkTimer = cfg.clawCombo.windup * s;
        this.atkTeleId = this.deps.telegraph.circle(
          pos.x + Math.sin(this.facing) * cfg.clawCombo.range * 0.6,
          pos.z + Math.cos(this.facing) * cfg.clawCombo.range * 0.6,
          cfg.clawCombo.range,
          this.atkTimer,
        );
        break;
      case "charge":
        this.atkTimer = cfg.charge.windup * s;
        break;
      case "howl":
        this.howlWindup = cfg.howl.windup * s;
        this.atkTimer = this.howlWindup;
        this.atkTeleId = this.deps.telegraph.circle(pos.x, pos.z, cfg.howl.radius, this.atkTimer, DANGER);
        break;
      case "leap": {
        this.atkTimer = cfg.leap.windup * s;
        this.leapRadius = this.isRage ? cfg.leap.radiusRage : cfg.leap.radius;
        // 10.9AB — mira ANTECIPADA: cai onde o player VAI estar (windup+airTime), não onde está
        // agora. Fator 0.9 deixa a queda um tico atrás (mais justo p/ esquivar, mas acerta quem foge).
        const lead = (cfg.leap.windup + cfg.leap.airTime) * this.atkScale * 0.9;
        this.predictPlayer(lead, this.target);
        this.atkTeleId = this.deps.telegraph.circle(
          this.target.x,
          this.target.z,
          this.leapRadius,
          this.atkTimer + cfg.leap.airTime * this.atkScale,
        );
        break;
      }
      case "spikes": {
        // 10.9AF — erupções telegrafadas: a 1ª na posição PREVISTA do player, as outras espalhadas
        // em volta (força o player a se mover). O dano resolve em updateSpikes ao fim do telegraph.
        const sp = cfg.spikes;
        this.atkTimer = sp.windup * s;
        const life = this.atkTimer;
        this.predictPlayer(life * 0.8, this.target);
        for (let i = 0; i < sp.count; i++) {
          const ang = Math.random() * Math.PI * 2;
          const rr = i === 0 ? 0 : Math.random() * sp.spread;
          const zx = this.target.x + Math.cos(ang) * rr;
          const zz = this.target.z + Math.sin(ang) * rr;
          this.deps.telegraph.circle(zx, zz, sp.radius, life, DANGER);
          this.pendingSpikes.push({ x: zx, z: zz, r: sp.radius, t: life, dmg: sp.damage });
        }
        break;
      }
    }
  }

  private updateAttack(dt: number): void {
    switch (this.attackName) {
      case "clawCombo":
        this.updateClaw(dt);
        break;
      case "charge":
        this.updateCharge(dt);
        break;
      case "howl":
        this.updateHowl(dt);
        break;
      case "leap":
        this.updateLeap(dt);
        break;
      case "spikes":
        this.updateSpikesAttack(dt);
        break;
    }
  }

  // ---- Void Spikes (ataque à distância) ----
  private updateSpikesAttack(dt: number): void {
    this.atkTimer -= dt;
    if (this.atkPhase === 0) {
      if (this.atkTimer <= 0) {
        this.deps.impulse(0.1); // o chão treme quando os espinhos irrompem (o dano é resolvido em updateSpikes)
        this.atkPhase = 1;
        this.atkTimer = cfg.spikes.recovery * this.atkScale;
      }
    } else if (this.atkTimer <= 0) {
      this.finishAttack("spikes");
    }
  }

  /** Resolve o dano das erupções quando o telegraph expira (roda todo frame, fora da luta ou não). */
  private updateSpikes(dt: number): void {
    if (this.pendingSpikes.length === 0) return;
    const p = this.deps.player;
    for (let i = this.pendingSpikes.length - 1; i >= 0; i--) {
      const z = this.pendingSpikes[i];
      z.t -= dt;
      if (z.t > 0) continue;
      this.pendingSpikes.splice(i, 1);
      this.deps.particles.burst(z.x, 0.3, z.z, DANGER, { count: 12, speed: 8, dur: 0.5 });
      const dx = p.position.x - z.x;
      const dz = p.position.z - z.z;
      if (dx * dx + dz * dz <= z.r * z.r) {
        dealDamage(
          p,
          { baseDamage: z.dmg * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 },
          { targetKind: "player", attackerRank: "boss", damageType: "blunt", knockbackFrom: new THREE.Vector3(z.x, 0, z.z), knockbackForce: 6 },
        );
      }
    }
  }

  private finishAttack(name: BossAttack): void {
    this.cd[name] = cfg[name].cooldown;
    this.restTimer = cfg.restBetweenAttacks * this.atkScale;
    this.attackName = null;
    this.state = "chase";
  }

  // ---- Claw Combo: 2 swipes + heavy ----
  private updateClaw(dt: number): void {
    const c = cfg.clawCombo;
    const s = this.atkScale;
    this.atkTimer -= dt;
    if (this.atkTimer > 0) return;
    if (this.atkPhase === 0) {
      this.clawSwipe(c.swipeDamage, c.arc, c.range, c.knockback * 0.5);
      this.atkPhase = 1;
      this.atkTimer = c.swipeGap * s;
    } else if (this.atkPhase === 1) {
      this.clawSwipe(c.swipeDamage, c.arc, c.range, c.knockback * 0.5);
      this.atkPhase = 2;
      this.atkTimer = c.heavyWindup * s;
      const pos = this.group.position;
      this.deps.telegraph.circle(
        pos.x + Math.sin(this.facing) * c.range * 0.7,
        pos.z + Math.cos(this.facing) * c.range * 0.7,
        c.range + 0.6,
        this.atkTimer,
      );
    } else if (this.atkPhase === 2) {
      this.clawSwipe(c.heavyDamage, c.arc, c.range + 0.6, c.knockback);
      this.deps.impulse(0.12);
      this.atkPhase = 3;
      this.atkTimer = c.recovery * s;
    } else {
      this.finishAttack("clawCombo");
    }
  }

  private clawSwipe(damage: number, arc: number, range: number, knockback: number): void {
    const pos = this.group.position;
    this.deps.particles.burst(
      pos.x + Math.sin(this.facing) * range * 0.7,
      1,
      pos.z + Math.cos(this.facing) * range * 0.7,
      DANGER,
      { count: 8, speed: 5 },
    );
    this.hitArc(damage, arc, range, knockback);
  }

  // ---- Void Charge ----
  private updateCharge(dt: number): void {
    const c = cfg.charge;
    if (this.atkPhase === 0) {
      // windup: encara o player; trava a direção ao final
      this.atkTimer -= dt;
      // 10.9AB — durante o windup aponta pra onde o player VAI estar (metade do trajeto da
      // investida), pra investida cruzar o caminho de quem foge em vez de passar por trás.
      const travel = cfg.charge.maxDist / (cfg.charge.speed * this.speedMult);
      this.faceInstant(this.predictPlayer(travel * 0.5, this.target));
      if (this.atkTimer <= 0) {
        this.chargeDir.set(Math.sin(this.facing), 0, Math.cos(this.facing));
        this.chargeTravelled = 0;
        this.chargeHitDone = false;
        // corredor visível pelo TEMPO TODO da investida (não some em 0.25s)
        const sweepDur = c.maxDist / (c.speed * this.speedMult);
        this.atkTeleId = this.deps.telegraph.lane(
          this.group.position.x,
          this.group.position.z,
          this.facing,
          c.maxDist,
          c.width,
          sweepDur,
          DANGER,
        );
        this.atkPhase = 1;
      }
    } else if (this.atkPhase === 1) {
      const step = c.speed * this.speedMult * dt;
      this.group.position.addScaledVector(this.chargeDir, step);
      this.chargeTravelled += step;
      // acerto (uma vez)
      if (!this.chargeHitDone) {
        const dx = this.deps.player.position.x - this.group.position.x;
        const dz = this.deps.player.position.z - this.group.position.z;
        if (dx * dx + dz * dz <= (c.width / 2 + 0.7) * (c.width / 2 + 0.7)) {
          this.hitAoE(this.group.position, c.width / 2 + 0.7, c.damage, c.knockback);
          this.chargeHitDone = true;
        }
      }
      // parede?
      if (this.wouldExitArena(this.group.position)) {
        this.clampToArena(this.group.position);
        this.deps.telegraph.cancel(this.atkTeleId);
        this.atkTeleId = 0;
        this.deps.impulse(0.16);
        this.deps.particles.burst(this.group.position.x, 1, this.group.position.z, DANGER, { count: 20, speed: 7, dur: 0.7 });
        this.atkPhase = 2;
        this.atkTimer = c.wallStagger; // vulnerável — janela de punição
      } else if (this.chargeTravelled >= c.maxDist) {
        this.deps.telegraph.cancel(this.atkTeleId);
        this.atkTeleId = 0;
        this.atkPhase = 3;
        this.atkTimer = c.recovery * this.atkScale;
      }
    } else if (this.atkPhase === 2) {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) {
        this.atkPhase = 3;
        this.atkTimer = c.recovery * this.atkScale;
      }
    } else {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) this.finishAttack("charge");
    }
  }

  // ---- Void Howl (+ summon) ----
  private updateHowl(dt: number): void {
    const c = cfg.howl;
    if (this.atkPhase === 0) {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) {
        // liberou o berro
        this.hitAoE(this.group.position, c.radius, c.damage, c.knockback);
        this.deps.impulse(0.18);
        this.deps.particles.burst(this.group.position.x, 1, this.group.position.z, DANGER, { count: 28, speed: 9, dur: 0.8 });
        this.deps.summon(c.summonCount, this.group.position);
        this.atkPhase = 1;
        this.atkTimer = c.recovery * this.atkScale;
      }
    } else {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) this.finishAttack("howl");
    }
  }

  private interruptHowl(): void {
    this.deps.telegraph.cancel(this.atkTeleId); // some com o círculo do berro interrompido
    this.atkTeleId = 0;
    this.attackName = null;
    this.atkPhase = 0;
    this.cd.howl = cfg.howl.cooldown;
    this.state = "stagger";
    this.stateTimer = cfg.interruptStagger;
    this.deps.particles.burst(this.group.position.x, this.radius * 1.4, this.group.position.z, 0x9d7bff, {
      count: 20,
      speed: 6,
    });
    events.emit("boss:interrupted", undefined);
  }

  // ---- Void Leap ----
  private updateLeap(dt: number): void {
    const c = cfg.leap;
    if (this.atkPhase === 0) {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) {
        this.leapStart.copy(this.group.position);
        this.leapT = 0;
        this.atkPhase = 1;
      }
    } else if (this.atkPhase === 1) {
      const air = c.airTime * this.atkScale;
      this.leapT += dt;
      const f = Math.min(1, this.leapT / air);
      this.group.position.lerpVectors(this.leapStart, this.target, f);
      this.group.position.y = Math.sin(Math.PI * f) * 4.5;
      if (f >= 1) {
        this.group.position.y = 0;
        this.hitAoE(this.target, this.leapRadius, c.damage, c.knockback);
        this.deps.impulse(0.2);
        this.deps.particles.burst(this.target.x, 0.5, this.target.z, DANGER, { count: 26, speed: 8, dur: 0.8 });
        this.atkPhase = 2;
        this.atkTimer = c.recovery * this.atkScale;
      }
    } else {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) this.finishAttack("leap");
    }
  }

  // ---- helpers de dano ----
  /** 10.9AC — fatia da vida MÁX do player somada a cada golpe (dano fixo virava cosquinha). */
  private chip(): number {
    return this.deps.player.health.max * cfg.hpChipPct;
  }

  private hitArc(damage: number, arc: number, range: number, knockback: number): void {
    const p = this.deps.player;
    _v.subVectors(p.position, this.group.position);
    _v.y = 0;
    const d = _v.length();
    if (d > range + 0.6 || d < 0.001) return;
    _v.multiplyScalar(1 / d);
    const dot = _v.x * Math.sin(this.facing) + _v.z * Math.cos(this.facing);
    if (dot < Math.cos(arc / 2)) return;
    dealDamage(
      p,
      { baseDamage: damage * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 },
      { targetKind: "player", attackerRank: "boss", damageType: "slashing", knockbackFrom: this.group.position, knockbackForce: knockback },
    );
  }

  private hitAoE(center: THREE.Vector3, radius: number, damage: number, knockback: number): void {
    const p = this.deps.player;
    const dx = p.position.x - center.x;
    const dz = p.position.z - center.z;
    if (dx * dx + dz * dz > radius * radius) return;
    dealDamage(
      p,
      { baseDamage: damage * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 },
      { targetKind: "player", attackerRank: "boss", damageType: "slashing", knockbackFrom: center, knockbackForce: knockback },
    );
  }

  // ---- antecipação de movimento (10.9AB) ----
  /** Velocidade suavizada do player (m/s no plano) — base p/ prever onde ele estará. */
  private trackPlayerVel(dt: number): void {
    const p = this.deps.player.position;
    if (this.velInit && dt > 0) {
      const k = Math.min(1, dt * 8); // suaviza (ignora micro-jitter de 1 frame)
      this.playerVel.x += ((p.x - this.lastPlayerPos.x) / dt - this.playerVel.x) * k;
      this.playerVel.z += ((p.z - this.lastPlayerPos.z) / dt - this.playerVel.z) * k;
    }
    this.lastPlayerPos.copy(p);
    this.velInit = true;
  }

  /** Onde o player deve estar daqui a `t` s (clampado à arena) — leap/charge miram AQUI. */
  private predictPlayer(t: number, out: THREE.Vector3): THREE.Vector3 {
    const p = this.deps.player.position;
    out.set(p.x + this.playerVel.x * t, 0, p.z + this.playerVel.z * t);
    this.clampToArena(out);
    return out;
  }

  // ---- posição / orientação ----
  private distToPlayer(): number {
    _v.subVectors(this.deps.player.position, this.group.position);
    _v.y = 0;
    return _v.length();
  }

  private updateFacing(dt: number): void {
    if (this.state === "dead") return;
    let target = this.facing;
    if (this.attackName === "charge" && this.atkPhase >= 1) {
      target = Math.atan2(this.chargeDir.x, this.chargeDir.z);
    } else if (this.state === "chase" || (this.state === "attack" && this.atkPhase === 0)) {
      _v.subVectors(this.deps.player.position, this.group.position);
      _v.y = 0;
      if (_v.lengthSq() > 0.01) target = Math.atan2(_v.x, _v.z);
    }
    this.facing = dampAngle(this.facing, target, 7, dt);
  }

  private faceInstant(pos: THREE.Vector3): void {
    _v.subVectors(pos, this.group.position);
    _v.y = 0;
    if (_v.lengthSq() > 0.01) this.facing = dampAngle(this.facing, Math.atan2(_v.x, _v.z), 12, 1 / 60);
  }

  private clampToArena(pos: THREE.Vector3): void {
    const cx = this.arenaCx;
    const cz = this.arenaCz;
    const dx = pos.x - cx;
    const dz = pos.z - cz;
    const d = Math.hypot(dx, dz);
    const lim = this.arenaR + GameConfig.boss.leash.margin - this.radius; // 10.9DA — segue o player por toda a zona (a coleira desiste se ele fugir de vez)
    if (d > lim && d > 0.001) {
      pos.x = cx + (dx / d) * lim;
      pos.z = cz + (dz / d) * lim;
    }
  }

  private wouldExitArena(pos: THREE.Vector3): boolean {
    const dx = pos.x - this.arenaCx;
    const dz = pos.z - this.arenaCz;
    return Math.hypot(dx, dz) >= this.arenaR - this.radius;
  }

  // ---- morte ----
  private die(): void {
    if (this.state === "dead") return;
    this.state = "dead";
    this.attackName = null;
    this.deathTimer = 1.6;
    this.deps.impulse(0.22);
    events.emit(this.isEvent ? "boss:defeatedEvent" : "boss:defeated", undefined);
  }

  private updateDeath(dt: number): void {
    this.deathTimer -= dt;
    const t = Math.max(0, this.deathTimer / 1.6);
    this.group.scale.setScalar(t);
    this.bodyMat.emissiveIntensity = 3 * t;
    this.auraMat.opacity = 0.4 * t;
    this.group.position.y += dt * 0.8;
    if (Math.random() < dt * 10) {
      this.deps.particles.burst(
        this.group.position.x + (Math.random() - 0.5) * 3,
        Math.random() * 3,
        this.group.position.z + (Math.random() - 0.5) * 3,
        AURA,
        { count: 6, speed: 4 },
      );
    }
    if (this.deathTimer <= 0) this.removeRequested = true;
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
    this.bodyMat.dispose();
    this.eyeMat.dispose();
    this.auraMat.dispose();
  }
}
